import { useEffect, useState } from 'react'
import { Button, Drawer, useDrawer } from '@iveco-ti/iveco-ds'
import { useTranslation } from 'react-i18next'

import { useDrawerManageAuthForms } from '../hooks/useDrawerManageAuthForms'
import { useGetApplicationAuth } from '../hooks/useGetApplicationAuth'
import { useRemoveApplicationAuth } from '../hooks/useRemoveApplicationAuth'
import { useManageApplicationStore } from '../stores/useManageApplicationStore'
import { DrawerManageAuthSkeleton } from './DrawerManageAuthSkeleton'
import { EditCredentialsForm } from './EditCredentialsForm'
import { ManageAuthForm } from './ManageAuthForm'

export function DrawerManageAuth() {
  const { t } = useTranslation(['sso', 'common'])
  const { close } = useDrawer('drawer-manage-auth')
  const { application } = useManageApplicationStore()
  const [editMode, setEditMode] = useState<
    'view' | 'edit-credentials' | 'edit-password'
  >('view')
  const [forceReset, setForceReset] = useState(false)

  const { data: authData, isLoading } = useGetApplicationAuth(
    application?.id || '',
  )

  const { handleRemoveApplicationAuth } = useRemoveApplicationAuth(close)

  const {
    credentialsForm,
    passwordForm,
    handleSubmitCredentials,
    handleSubmitPassword,
    resetForms,
  } = useDrawerManageAuthForms({
    authData,
    editMode,
    setEditMode,
  })

  const hasExistingAuth =
    authData && Object.keys(authData).length > 0 && authData.id

  const handleClose = () => {
    setEditMode('view')
    resetForms()
    setForceReset(true)
    setTimeout(() => setForceReset(false), 100)
    close()
  }

  const handleCancelAuth = () => {
    if (editMode !== 'view') {
      setEditMode('view')
      resetForms()
    } else {
      handleClose()
    }
  }

  const handleStartEditCredentials = () => {
    if (!hasExistingAuth || !authData) return

    credentialsForm.reset({
      clientKey: authData.clientKey,
      currentPassword: '',
    })

    setEditMode('edit-credentials')
  }

  useEffect(() => {
    setEditMode('view')
  }, [application?.id])

  return (
    <>
      <Drawer.Root id="drawer-manage-auth">
        <Drawer.Header>
          <h1 className="text-lg text-brand-800 font-semibold">
            {t('sso:applications.drawer_manage_auth.header.title')}
          </h1>
        </Drawer.Header>

        <Drawer.Body>
          <h2 className="text-2xl font-semibold text-brand-700">
            {editMode !== 'view'
              ? t(
                  'sso:applications.drawer_manage_auth.edit_credentials.modal.title',
                )
              : t('sso:applications.drawer_manage_auth.body.title')}
          </h2>

          {isLoading && <DrawerManageAuthSkeleton />}

          {!isLoading && editMode === 'view' && (
            <ManageAuthForm
              application={application}
              authData={authData}
              hasExistingAuth={!!hasExistingAuth}
              onStartEditCredentials={handleStartEditCredentials}
              onClose={handleClose}
              forceReset={forceReset}
              key={`manage-auth-${application?.id}-${!!authData}-${editMode}`}
            />
          )}

          {!isLoading && editMode !== 'view' && authData && (
            <EditCredentialsForm
              credentialsForm={credentialsForm}
              passwordForm={passwordForm}
              onSubmitCredentials={handleSubmitCredentials}
              onSubmitPassword={handleSubmitPassword}
              activeTab={
                editMode === 'edit-credentials' ? 'credentials' : 'password'
              }
              onActiveTabChange={(tab) =>
                setEditMode(
                  tab === 'credentials' ? 'edit-credentials' : 'edit-password',
                )
              }
            />
          )}
        </Drawer.Body>

        <Drawer.Footer>
          <div className="flex items-center justify-between">
            <div>
              {!isLoading &&
                hasExistingAuth &&
                authData &&
                Object.keys(authData).length > 0 &&
                editMode === 'view' && (
                  <Button.Root
                    variant="secondary"
                    color="danger"
                    onClick={() => handleRemoveApplicationAuth(authData.id)}
                  >
                    {t(
                      'sso:applications.drawer_manage_auth.remove_auth_button.label',
                    )}
                  </Button.Root>
                )}
            </div>

            <div className="flex items-center gap-4">
              <Button.Root
                variant="secondary"
                color="brand"
                onClick={handleCancelAuth}
              >
                {t(
                  'sso:applications.drawer_manage_auth.footer.close_button.label',
                )}
              </Button.Root>

              {!isLoading && editMode === 'view' && !hasExistingAuth && (
                <Button.Root
                  variant="primary"
                  color="brand"
                  form="manage-auth-form"
                  type="submit"
                >
                  {t(
                    'sso:applications.drawer_manage_auth.footer.create_button.label',
                  )}
                </Button.Root>
              )}

              {!isLoading && editMode === 'view' && hasExistingAuth && (
                <Button.Root
                  variant="primary"
                  color="brand"
                  form="manage-auth-form"
                  type="submit"
                >
                  {t(
                    'sso:applications.drawer_manage_auth.footer.update_button.label',
                  )}
                </Button.Root>
              )}

              {!isLoading && editMode !== 'view' && (
                <Button.Root
                  variant="primary"
                  color="brand"
                  onClick={() => {
                    if (editMode === 'edit-credentials') {
                      credentialsForm.handleSubmit(handleSubmitCredentials)()
                    } else {
                      passwordForm.handleSubmit(handleSubmitPassword)()
                    }
                  }}
                >
                  {t(
                    'sso:applications.drawer_manage_auth.edit_credentials.modal.actions.save_button.label',
                  )}
                </Button.Root>
              )}
            </div>
          </div>
        </Drawer.Footer>
      </Drawer.Root>
    </>
  )
}
